import { createAsyncThunk } from '@reduxjs/toolkit';
import { userDataSelector } from 'app/auth/store/user.selector';
import { STORAGE_KEYS } from 'app/constants/storage.constant';
import { TagFormItemInDTO } from 'app/fuse-layouts/layout1/type';
import { ClientsSortOrderEnum, ClientsSortsUrlEnum } from 'app/main/pages/clients/type';
import { sendMediaOrThrow } from 'app/services/mediaService/mediaService';
import { AppDispatch, RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import axios from 'axios';
import i18n from 'i18n';
import { getMinimalStoreListApi, updateSubscriptionAPI } from '../../admin/api';
import { getMerchantListPageAPI } from '../../admin/api/merchantList';
import { setMerchantListTotal } from '../../admin/ducks/merchantListFilters.slice';
import { pruneUnknownMerchantCategory } from '../../admin/ducks/merchantListFilters.thunk';
import {
  FranchiseStatusEnum,
  MerchantAndStoreSortEnum,
  MerchantAndStoreUrlEnum,
  PartnerFilterEnum,
} from '../../admin/types/type';
import { toMerchantListApiParams } from '../../admin/utils/merchantFilterParams';
import { getFranchiseTagsApi, patchTagApi } from '../../franchises/api';
import { getLoyaltyModel } from '../../loyalties/ducks/loyalty.thunk';
import {
  clearStoreSalesReceptionErrorsApi,
  createPosMerchantAccountApi,
  createStoreApi,
  getPosMerchantAccountApi,
  getStoreSalesReceptionStatusApi,
  getStoresApi,
  patchStoreSettingsApi,
  removeStoreApi,
  updatePosMerchantAccountApi,
  updateStoreApi,
} from '../api';
import { PosMerchantAccountDTO, StoreDTO, StoreSettingsDTO } from '../type';
import { formatHoursToDTO } from '../utils';
import {
  merchantAndStoresFilterSelector,
  merchantAndStoresPaginationSelector,
  merchantAndStoresPartnerFilterSelector,
  merchantAndStoresSortSelector,
  merchantAndStoresStatusFilterSelector,
  selectStores,
  storesDialogSelector,
} from './stores.selector';
import {
  actions,
  CLIENTS_PARTNER_DEFAULT,
  CLIENTS_SORT_DEFAULT,
  CLIENTS_SORT_ORDER_DEFAULT,
  CLIENTS_STATUS_DEFAULT,
  PAGE,
  ROWS_PER_PAGE,
} from './stores.slice';

export const getSalesReceptionStatusThunk = createAsyncThunk(
  'storesApp/stores/getSalesReceptionStatus',
  async (storeId: string, { dispatch }) => {
    try {
      const status = await getStoreSalesReceptionStatusApi(storeId);
      dispatch(actions.setSalesReceptionStatus(status));
    } catch {
      dispatch(actions.setSalesReceptionStatus(null));
    }
  },
);

export const clearSalesReceptionErrorsThunk = createAsyncThunk(
  'storesApp/stores/clearSalesReceptionErrors',
  async (storeId: string, { dispatch }) => {
    const status = await clearStoreSalesReceptionErrorsApi(storeId);
    dispatch(actions.setSalesReceptionStatus(status));
  },
);

export const openNewStoreDialog = () => (dispatch: any, getState: () => any) => {
  const user = userDataSelector(getState());
  dispatch(actions.openNewStoreDialog(user.id));
};

export const openNewStoreAdminDialog = (isFirst: boolean, userId?: string) => (dispatch: AppDispatch) => {
  dispatch(actions.openNewStoreAdminDialog({ isFirst, userId }));
};

export const openEditStoreDialog = (rowData: any) => (dispatch: AppDispatch) => {
  dispatch(actions.openEditStoreDialog(rowData));
};

export const openPosDialog = () => (dispatch: AppDispatch) => dispatch(actions.openPosDialog());

export const closePosDialog = () => (dispatch: AppDispatch) => dispatch(actions.closePosDialog());

export const openTagsDialog = () => (dispatch: AppDispatch) => dispatch(actions.openTagsDialog());

export const closeTagsDialog = () => (dispatch: AppDispatch) => dispatch(actions.closeTagsDialog());

export const closeStoreDialog = () => (dispatch: AppDispatch) => dispatch(actions.closeStoreDialog());

export const openConfirmDeleteDialog = (payload: any) => (dispatch: AppDispatch) => {
  dispatch(actions.openConfirmDeleteDialog(payload));
};

export const getStores = () => async (dispatch: any) => {
  try {
    const storesDto: StoreDTO[] = await getStoresApi();
    dispatch(actions.getStoresSuccess({ data: storesDto }));
    return storesDto;
  } catch (_e) {
    dispatch(actions.getStoresFailed());
    return [];
  }
};

export const getStoreById = createAsyncThunk('storesApp/stores/getStoreById', async (payload: any) => {
  const { storeId } = payload;
  const response = await axios.get(`api/Stores/${storeId}`);

  const data = await response.data;

  return data;
});

// A network failure has no HTTP status; the table still has to show its error state.
const API_UNREACHABLE_CODE_ERROR = 503;

const PARTNER_FILTER_QUERY: Record<PartnerFilterEnum, boolean | undefined> = {
  [PartnerFilterEnum.ALL]: undefined,
  [PartnerFilterEnum.PARTNER]: true,
  [PartnerFilterEnum.NON_PARTNER]: false,
};

const isPartnerFilter = (value: string | null): value is PartnerFilterEnum =>
  Object.values(PartnerFilterEnum).includes(value as PartnerFilterEnum);

// Filters can change faster than the list answers: only the latest request may update the list.
let latestMerchantListRequestId = 0;

export const getMerchantAndStores = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  latestMerchantListRequestId += 1;
  const requestId = latestMerchantListRequestId;
  const isStale = () => requestId !== latestMerchantListRequestId;
  try {
    dispatch(actions.setMerchantAndStoresIsLoading(true));
    const filter = merchantAndStoresFilterSelector(getState());
    const statusFilter = merchantAndStoresStatusFilterSelector(getState());
    const sort = merchantAndStoresSortSelector(getState());
    const pagination = merchantAndStoresPaginationSelector(getState());
    const disabled: boolean | undefined =
      statusFilter === FranchiseStatusEnum.ACTIVE
        ? false
        : statusFilter === FranchiseStatusEnum.INACTIVE
          ? true
          : undefined;
    const isPartner = PARTNER_FILTER_QUERY[merchantAndStoresPartnerFilterSelector(getState())];
    const listFilters = dispatch(pruneUnknownMerchantCategory());

    const merchantListPage = await getMerchantListPageAPI({
      filter,
      isDisabled: disabled,
      isPartner,
      sort: sort.type,
      sortOrder: sort.order,
      page: pagination.page,
      size: pagination.rowsPerPage,
      ...toMerchantListApiParams(listFilters),
    });
    if (isStale()) {
      return;
    }
    dispatch(actions.setMerchantAndStoresCodeError(null));
    dispatch(setMerchantListTotal(merchantListPage.metadata.total));
    const lastPage = Math.ceil(merchantListPage.metadata.total / pagination.rowsPerPage);
    // A link kept from a longer list can point past its end: land on the last page, not an empty one.
    if (lastPage > 0 && pagination.page > lastPage) {
      dispatch(replaceMerchantListPage(lastPage));
      return;
    }
    dispatch(actions.setMerchantAndStores(merchantListPage.data));
  } catch (error) {
    if (isStale()) {
      return;
    }
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    dispatch(actions.setMerchantAndStoresCodeError(status ?? API_UNREACHABLE_CODE_ERROR));
    dispatch(setMerchantListTotal(0));
  } finally {
    if (!isStale()) {
      dispatch(actions.setMerchantAndStoresIsLoading(false));
    }
  }
};

export const createStore = createAsyncThunk(
  'storesApp/stores/createStore',
  async ({ form, picture }: { form: any; logo?: File; picture?: File }, { dispatch, getState }: any) => {
    dispatch(actions.setStoreDialogIsLoading(true));
    try {
      const state = getState() as RootState;
      const { userId, type } = storesDialogSelector(state);
      const { addressDTO, position } = form.address;
      const pictureURL = picture ? await sendMediaOrThrow(picture) : form.picture;

      const request = {
        name: form.name,
        description: form.description,
        phone: form.phone,
        email: form.email,
        website: form.website,
        clickNCollectLink: form.clickNCollectLink,
        menuLink: form.menuLink,
        logo: '',
        ctNum: form.ctNum,
        picture: pictureURL,
        location: position,
        communityId: '',
        groupId: '',
        currencyCode: '',
        facebookLink: form.facebookLink,
        instagramLink: form.instagramLink,
        twitterLink: form.twitterLink,
        googleReviewLink: form.googleReviewLink,
        userId: userId || form.userId,
        categoryId: form.categoryId,
        providerId: form.providerId,
        storeAddress: addressDTO,
        storeOpeningHours: {
          hours: formatHoursToDTO(form.hours),
        },
      } as StoreDTO;
      const store = await createStoreApi(request);

      if (type === 'admin') {
        dispatch(getMerchantAndStores());
      } else {
        dispatch(getStores());
        dispatch(getLoyaltyModel());
      }

      dispatch(actions.closeStoreDialog());

      return { data: store };
    } catch {
      dispatch(
        showMessage({
          message: 'errors.generic', // key, text or html
          autoHideDuration: 3000, // ms
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          variant: 'error', // success error info warning null
        }),
      );
      return { data: [] };
    } finally {
      dispatch(actions.setStoreDialogIsLoading(false));
    }
  },
);

export const updateStore = createAsyncThunk(
  'storesApp/stores/updateStore',
  async ({ form, logo, picture }: { form: any; logo: File; picture: File }, { dispatch, getState }) => {
    dispatch(actions.setStoreDialogIsLoading(true));
    const state = getState() as RootState;
    const { data } = storesDialogSelector(state);
    if (!data) {
      dispatch(
        showMessage({
          message: 'errors.generic', // key, text or html
          autoHideDuration: 3000, // ms
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          variant: 'error', // success error info warning null
        }),
      );
      throw new Error('No store data');
    }
    const storeId = data?.id || '';
    try {
      const { addressDTO, position } = form.address;
      const logoUrl = logo ? await sendMediaOrThrow(logo) : form.logo;
      const pictureUrl = picture ? await sendMediaOrThrow(picture) : form.picture;
      const request: Partial<StoreDTO> = {
        name: form.name,
        description: form.description,
        phone: form.phone,
        email: form.email,
        website: form.website,
        clickNCollectLink: form.clickNCollectLink,
        menuLink: form.menuLink,
        logo: logoUrl,
        ctNum: form.ctNum,
        picture: pictureUrl,
        currencyCode: data.currencyCode,
        facebookLink: form.facebookLink,
        instagramLink: form.instagramLink,
        twitterLink: form.twitterLink,
        tikTokLink: form.tikTokLink,
        googleReviewLink: form.googleReviewLink,
        userId: data?.userId,
        location: position || { lng: data?.location.coordinates[0], lat: data?.location.coordinates[1] },
        categoryId: form.categoryId,
        providerId: form.providerId,
        storeAddress: {
          ...(data?.storeAddress || {}),
          ...addressDTO,
          id: data?.storeAddress.id || '',
        },
        storeOpeningHours: {
          hours: formatHoursToDTO(form.hours),
        },
      } as StoreDTO;
      const store = await updateStoreApi(storeId, request);

      dispatch(getStores());
      dispatch(actions.closeStoreDialog());
      return { data: store };
    } catch {
      dispatch(
        showMessage({
          message: 'errors.generic', // key, text or html
          autoHideDuration: 3000, // ms
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          variant: 'error', // success error info warning null
        }),
      );
      return { data: [] };
    } finally {
      dispatch(actions.setStoreDialogIsLoading(false));
    }
  },
);

export const removeStore = createAsyncThunk('storesApp/stores/removeStores', async (storeId: string, { dispatch }) => {
  dispatch(actions.setConfirmDeleteDialogIsLoading(true));
  try {
    await removeStoreApi(storeId);
    dispatch(showMessage({ message: i18n.t('pages.validation.alert.storeDeleted'), variant: 'success' }));
    dispatch(getStores());
  } catch (_e: any) {
    dispatch(
      showMessage({
        message: i18n.t('errors.generic', { ns: 'store' }),
        variant: 'error',
      }),
    );
  }
  dispatch(actions.closeConfirmDeleteDialog());
});

export const resetSearchBar =
  (setTempSearchText: React.Dispatch<React.SetStateAction<string>>) => (dispatch: AppDispatch) => {
    dispatch(actions.setMerchantAndStoresFilter(''));
    setTempSearchText('');
    const url = new URL(window.location.href);
    url.searchParams.delete(MerchantAndStoreUrlEnum.search);
    url.searchParams.set(MerchantAndStoreUrlEnum.page, PAGE.toString());
    dispatch(actions.setMerchantAndStoresPagination({ page: PAGE }));
    window.history.pushState({}, '', url.toString());
  };

export const onSearchChange = (tempSearchText: string) => (dispatch: AppDispatch, getState: () => RootState) => {
  if (tempSearchText === (merchantAndStoresFilterSelector(getState()) ?? '')) {
    return;
  }
  dispatch(actions.setMerchantAndStoresFilter(tempSearchText));
  const url = new URL(window.location.href);
  url.searchParams.delete(MerchantAndStoreUrlEnum.search);
  if (tempSearchText) {
    url.searchParams.append(MerchantAndStoreUrlEnum.search, tempSearchText);
    url.searchParams.set(MerchantAndStoreUrlEnum.page, PAGE.toString());
    dispatch(actions.setMerchantAndStoresPagination({ page: PAGE }));
  }
  window.history.pushState({}, '', url.toString());
};

const replaceMerchantListPage = (page: number) => (dispatch: AppDispatch) => {
  const url = new URL(window.location.href);
  url.searchParams.set(MerchantAndStoreUrlEnum.page, page.toString());
  window.history.replaceState({}, '', url.toString());
  dispatch(actions.setMerchantAndStoresPagination({ page }));
};

export const clientsHandleChangePage = (newPage: number) => (dispatch: AppDispatch) => {
  const url = new URL(window.location.href);
  url.searchParams.set(MerchantAndStoreUrlEnum.page, newPage.toString());
  window.history.pushState({}, '', url.toString());
  dispatch(actions.setMerchantAndStoresPagination({ page: newPage }));
};

export const clientsHandleChangeStatusFilter = (status: FranchiseStatusEnum) => (dispatch: AppDispatch) => {
  const url = new URL(window.location.href);
  url.searchParams.set(MerchantAndStoreUrlEnum.status, status);
  url.searchParams.set(MerchantAndStoreUrlEnum.page, PAGE.toString());
  window.history.pushState({}, '', url.toString());
  dispatch(actions.setMerchantAndStoresPagination({ page: PAGE }));
  dispatch(actions.setMerchantAndStoresStatusFilter(status));
};

export const clientsHandleChangePartnerFilter = (partner: PartnerFilterEnum) => (dispatch: AppDispatch) => {
  const url = new URL(window.location.href);
  url.searchParams.set(MerchantAndStoreUrlEnum.partner, partner);
  url.searchParams.set(MerchantAndStoreUrlEnum.page, PAGE.toString());
  window.history.pushState({}, '', url.toString());
  dispatch(actions.setMerchantAndStoresPagination({ page: PAGE }));
  dispatch(actions.setMerchantAndStoresPartnerFilter(partner));
};

export const clientsHandleSort =
  (key: MerchantAndStoreSortEnum, sort?: MerchantAndStoreSortEnum, sortOrder?: ClientsSortOrderEnum) =>
  (dispatch: AppDispatch) => {
    const url = new URL(window.location.href);
    if (sort === key) {
      const newOrder =
        sortOrder === ClientsSortOrderEnum.descending
          ? ClientsSortOrderEnum.ascending
          : sortOrder === ClientsSortOrderEnum.ascending
            ? undefined
            : ClientsSortOrderEnum.descending;
      dispatch(
        actions.setMerchantAndStoresSort({
          order: newOrder,
        }),
      );
      if (newOrder) {
        url.searchParams.set(ClientsSortsUrlEnum.order, newOrder);
      } else {
        dispatch(actions.setMerchantAndStoresSort({ type: CLIENTS_SORT_DEFAULT, order: CLIENTS_SORT_ORDER_DEFAULT }));
        url.searchParams.delete(ClientsSortsUrlEnum.sort);
        url.searchParams.delete(ClientsSortsUrlEnum.order);
      }
    } else {
      dispatch(actions.setMerchantAndStoresSort({ type: key, order: ClientsSortOrderEnum.descending }));
      url.searchParams.set(ClientsSortsUrlEnum.sort, key);
      url.searchParams.set(ClientsSortsUrlEnum.order, ClientsSortOrderEnum.descending);
    }
    window.history.pushState({}, '', url.toString());
  };

// The merchant list route refuses a page size above 100.
const MAX_ROWS_PER_PAGE = 100;

const isValidRowsPerPage = (value: string | null) => {
  const rowsPerPage = Number(value);
  return value !== null && Number.isInteger(rowsPerPage) && rowsPerPage >= 1 && rowsPerPage <= MAX_ROWS_PER_PAGE;
};

export const initMerchantAndStoresParams = () => (dispatch: AppDispatch) => {
  const url = new URL(window.location.href);

  if (!url.searchParams.get(MerchantAndStoreUrlEnum.page)) {
    url.searchParams.set(MerchantAndStoreUrlEnum.page, PAGE.toString());
  }

  if (!isValidRowsPerPage(url.searchParams.get(MerchantAndStoreUrlEnum.rowsPerPage))) {
    url.searchParams.set(MerchantAndStoreUrlEnum.rowsPerPage, ROWS_PER_PAGE.toString());
  }

  if (!url.searchParams.get(ClientsSortsUrlEnum.sort)) {
    url.searchParams.set(ClientsSortsUrlEnum.sort, CLIENTS_SORT_DEFAULT);
  }

  if (!url.searchParams.get(ClientsSortsUrlEnum.order)) {
    url.searchParams.set(ClientsSortsUrlEnum.order, CLIENTS_SORT_ORDER_DEFAULT);
  }

  if (!url.searchParams.get(MerchantAndStoreUrlEnum.status)) {
    url.searchParams.set(MerchantAndStoreUrlEnum.status, CLIENTS_STATUS_DEFAULT);
  }

  const partnerParam = url.searchParams.get(MerchantAndStoreUrlEnum.partner);
  if (!isPartnerFilter(partnerParam)) {
    url.searchParams.set(MerchantAndStoreUrlEnum.partner, CLIENTS_PARTNER_DEFAULT);
  }

  window.history.pushState({}, '', url.toString());

  dispatch(
    actions.setMerchantAndStoresFilterSortPagination({
      filter: url.searchParams.get(MerchantAndStoreUrlEnum.search) || '',
      statusFilter:
        (url.searchParams.get(MerchantAndStoreUrlEnum.status) as FranchiseStatusEnum) || CLIENTS_STATUS_DEFAULT,
      partnerFilter: isPartnerFilter(partnerParam) ? partnerParam : CLIENTS_PARTNER_DEFAULT,
      pagination: {
        page: parseInt(url.searchParams.get(MerchantAndStoreUrlEnum.page) || PAGE.toString(), 10),
        rowsPerPage: parseInt(
          url.searchParams.get(MerchantAndStoreUrlEnum.rowsPerPage) || ROWS_PER_PAGE.toString(),
          10,
        ),
      },
      sort: {
        type: (url.searchParams.get(ClientsSortsUrlEnum.sort) as MerchantAndStoreSortEnum) || CLIENTS_SORT_DEFAULT,
        order: (url.searchParams.get(ClientsSortsUrlEnum.order) as ClientsSortOrderEnum) || CLIENTS_SORT_ORDER_DEFAULT,
      },
    }),
  );
};

export const updateSubscription = createAsyncThunk(
  'adminApp/users/updateSubscription',
  async ({ userId, subscriptionStatus }: { userId: string; subscriptionStatus: boolean }, { dispatch }) => {
    try {
      dispatch(actions.updateMerchantAndStoresIsLoading({ userId, isLoading: true }));
      const subscription = await updateSubscriptionAPI(userId, subscriptionStatus);
      dispatch(actions.updateMerchantAndStores({ userId, subscription }));
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateSuccessfully'), variant: 'success' }));
    } catch (_e) {
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateFailed'), variant: 'error' }));
    } finally {
      dispatch(actions.updateMerchantAndStoresIsLoading({ userId, isLoading: false }));
    }
  },
);

export const getPosMerchantAccount = createAsyncThunk(
  'storesApp/stores/getPosMerchantAccount',
  async (storeId: string, { dispatch }) => {
    try {
      const posMerchantAccount = await getPosMerchantAccountApi(storeId);
      dispatch(actions.setPosMerchantAccount(posMerchantAccount));
    } catch (e: any) {
      if (e?.response?.data?.error?.status === 'NOT_FOUND') {
        return;
      }
      dispatch(
        showMessage({
          message: i18n.t('errors.fetchIntegrationFailed', { ns: 'store' }),
          variant: 'error',
        }),
      );
    }
  },
);

// The backend probes the merchant's POS on every save, so its refusal names which field is
// wrong. A generic "update failed" threw that away, and a wrong URL or database is exactly
// what a self-hosted setup gets wrong on the first try.
const POS_SAVE_ERROR_KEYS: { [code: string]: string } = {
  ODOO_INVALID_BASE_URL: 'dialogs.pos.errors.invalidBaseUrl',
  ODOO_INSECURE_BASE_URL: 'dialogs.pos.errors.insecureBaseUrl',
  ODOO_INVALID_DOMAIN: 'dialogs.pos.errors.invalidDomain',
  ODOO_INVALID_PROVIDER_STORE_ID: 'dialogs.pos.errors.invalidProviderStoreId',
  ODOO_BAD_CREDENTIALS: 'dialogs.pos.errors.badCredentials',
  ODOO_REQUEST_REJECTED: 'dialogs.pos.errors.requestRejected',
  ODOO_UNREACHABLE: 'dialogs.pos.errors.unreachable',
  ODOO_KEY_NOT_ADMIN: 'dialogs.pos.errors.keyNotAdmin',
  STORE_ADDRESS_NOT_FOUND: 'dialogs.pos.errors.storeAddressNotFound',
};

export const posSaveErrorMessage = (error: any): string => {
  const key = POS_SAVE_ERROR_KEYS[error?.response?.data?.error?.status];
  return key ? i18n.t(key) : i18n.t('pages.validation.alert.updateFailed');
};

export const createPosMerchantAccount = createAsyncThunk(
  'storesApp/stores/createPosMerchantAccount',
  async ({ storeId, form }: { storeId: string; form: any }, { dispatch }) => {
    try {
      dispatch(actions.setPosDialogIsLoading(true));
      form.specificPosData.posType = form.type;
      const data: Partial<PosMerchantAccountDTO> = {
        type: form.type,
        storeId,
        providerStoreId: form.providerStoreId,
        specificPosData: form.specificPosData,
        canSync: form.canSync,
        canManageCurrentSale: form.canManageCurrentSale,
        canRedeemLoyaltyOffer: form.canRedeemLoyaltyOffer,
        hasClickAndCollect: form.hasClickAndCollect,
        resetDelay: form.resetDelay,
        linkReceiptDelay: form.linkReceiptDelay,
      };
      const response = await createPosMerchantAccountApi(data);
      dispatch(actions.setPosMerchantAccount(response));
      dispatch(actions.closePosDialog());
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateSuccessfully'), variant: 'success' }));
    } catch (e: any) {
      dispatch(showMessage({ message: posSaveErrorMessage(e), variant: 'error', autoHideDuration: 8000 }));
    } finally {
      dispatch(actions.setPosDialogIsLoading(false));
    }
  },
);

export const updatePosMerchantAccount = createAsyncThunk(
  'storesApp/stores/updatePosMerchantAccount',
  async (
    { storeId, posMerchantAccountId, form }: { storeId: string; posMerchantAccountId: string; form: any },
    { dispatch },
  ) => {
    try {
      dispatch(actions.setPosDialogIsLoading(true));
      form.specificPosData.posType = form.type;
      const data: Partial<PosMerchantAccountDTO> = {
        id: posMerchantAccountId,
        type: form.type,
        storeId,
        providerStoreId: form.providerStoreId,
        specificPosData: form.specificPosData,
        canSync: form.canSync,
        canManageCurrentSale: form.canManageCurrentSale,
        canRedeemLoyaltyOffer: form.canRedeemLoyaltyOffer,
        hasClickAndCollect: form.hasClickAndCollect,
        resetDelay: form.resetDelay,
        linkReceiptDelay: form.linkReceiptDelay,
      };
      const response = await updatePosMerchantAccountApi(data);
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateSuccessfully'), variant: 'success' }));
      dispatch(actions.setPosMerchantAccount(response));
      dispatch(actions.closePosDialog());
    } catch (e: any) {
      dispatch(showMessage({ message: posSaveErrorMessage(e), variant: 'error', autoHideDuration: 8000 }));
    } finally {
      dispatch(actions.setPosDialogIsLoading(false));
    }
  },
);

export const getStoreTags = createAsyncThunk(
  'storesApp/stores/getStoreTags',
  async (
    {
      franchiseId,
      storeId,
      includeDisabled,
    }: {
      franchiseId: string;
      storeId: string;
      includeDisabled?: boolean;
    },
    { dispatch },
  ) => {
    try {
      dispatch(actions.setStoreTagsIsLoading({ storeId, isLoading: true }));
      const response = await getFranchiseTagsApi(franchiseId, storeId, includeDisabled);
      dispatch(actions.setStoreTagsHasSuccess({ storeId, tags: response }));
    } catch (_e: any) {
      dispatch(actions.setStoreTagsHasError({ storeId, hasError: true }));
    } finally {
      dispatch(actions.setStoreTagsIsLoading({ storeId, isLoading: false }));
    }
  },
);

export const editStoreTags = createAsyncThunk(
  'storesApp/stores/editStoreTags',
  async (
    {
      franchiseId,
      storeId,
      tags,
    }: {
      franchiseId: string;
      storeId: string;
      tags: TagFormItemInDTO[];
    },
    { dispatch },
  ) => {
    try {
      dispatch(actions.setTagsDialogIsLoading(true));
      const promises = tags.map(tag => patchTagApi(tag.tagId, tag));
      await Promise.all(promises);
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateSuccessfully'), variant: 'success' }));
      dispatch(getStoreTags({ franchiseId, storeId, includeDisabled: true }));
      dispatch(actions.closeTagsDialog());
    } catch (_e: any) {
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateFailed'), variant: 'error' }));
    } finally {
      dispatch(actions.setTagsDialogIsLoading(false));
    }
  },
);

export const editStoreSettings =
  ({ storeId, settings }: { storeId: string; settings: Partial<StoreSettingsDTO> }) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(actions.setStoreSettingsIsLoading(true));
      const response = await patchStoreSettingsApi(storeId, settings);
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateSuccessfully'), variant: 'success' }));
      dispatch(actions.setStoreSettings({ storeId, settings: response }));
    } catch (_e: any) {
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateFailed'), variant: 'error' }));
    } finally {
      dispatch(actions.setStoreSettingsIsLoading(false));
    }
  };

export const fetchMinimalStoreListThunk = createAsyncThunk(
  'admin/fetchMinimalStoreList',
  async (franchiseId: string, { dispatch, rejectWithValue }) => {
    try {
      const storeList = await getMinimalStoreListApi(franchiseId);
      return storeList;
    } catch (e) {
      dispatch(
        showMessage({
          message: i18n.t('pages.franchisePanel.errorLoadingStores', { ns: 'admin' }),
          variant: 'error',
        }),
      );
      return rejectWithValue(e);
    }
  },
);

export const setCurrentStoreIdThunk = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const stores = selectStores(getState());
  const currentStoreId = localStorage.getItem(STORAGE_KEYS.CURRENT_STORE_ID);
  if (currentStoreId) {
    dispatch(actions.setCurrentStoreId(currentStoreId));
  } else {
    const initialStoreId = stores[0]?.id || '';
    localStorage.setItem(STORAGE_KEYS.CURRENT_STORE_ID, initialStoreId);
    dispatch(actions.setCurrentStoreId(initialStoreId));
  }
};
